import React from "react";
import { Box, Typography } from "@mui/material";
import { LuChevronLeft, LuChevronRight, LuPalette } from "react-icons/lu";
import { formattedAmountWithoutAed } from "../../../utils/CommonFunction";
import { renderCurrencyIconColored } from "../../../utils/CurrencyUtils";

const InfoRow = ({ label, value }) => (
    <Box
        sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            padding: "10px 0",
            borderBottom: "1px solid #F3F4F6",
        }}
    >
        <Typography
            sx={{
                fontSize: "13px",
                color: "#949daa",
                fontFamily: "Inter Tight",
            }}
        >
            {label}
        </Typography>
        <Typography
            sx={{
                fontSize: "13px",
                fontWeight: 500,
                color: "#0d0d0e",
                fontFamily: "Inter Tight",
                textAlign: "right",
            }}
        >
            {value || "-"}
        </Typography>
    </Box>
);

// Right project drawer for the design bid flow — slides open/closed with
// a floating edge toggle, like a proper drawer rather than a static panel.
export default function ProjectDrawer({
    project,
    proposalAmount,
    status,
    isOpen,
    onToggle,
}) {
    const imageUrl =
        project?.project_image?.path ||
        project?.project_image?.image ||
        project?.default_image?.[0]?.image;

    const statusLabel = status
        ? status
              .replace(/[-_]/g, " ")
              .toLowerCase()
              .split(" ")
              .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
              .join(" ")
        : "-";

    return (
        <Box
            sx={{
                position: "relative",
                flexShrink: 0,
                width: isOpen ? "320px" : "16px",
                transition: "width 0.3s ease",
            }}
        >
            {/* Edge toggle — floats on the drawer's left border */}
            <Box
                component="button"
                type="button"
                onClick={onToggle}
                title={isOpen ? "Collapse" : "Expand"}
                sx={{
                    position: "absolute",
                    left: "-14px",
                    top: "40px",
                    zIndex: 3,
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    border: "1px solid #E5E7EB",
                    backgroundColor: "#fff",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "#6B7280",
                    transition: "transform 0.2s ease",
                    "&:hover": {
                        color: "#0d0d0e",
                        transform: "scale(1.08)",
                    },
                }}
            >
                {isOpen ? (
                    <LuChevronRight size={16} />
                ) : (
                    <LuChevronLeft size={16} />
                )}
            </Box>

            {/* Drawer body */}
            <Box
                sx={{
                    height: "100%",
                    borderLeft: "1px solid #EEF0F3",
                    borderRadius: "16px 0 0 16px",
                    backgroundColor: "#FBFCFE",
                    boxShadow: isOpen
                        ? "-8px 0 24px rgba(13, 13, 14, 0.04)"
                        : "none",
                    overflow: "hidden",
                    opacity: isOpen ? 1 : 0,
                    transform: isOpen ? "translateX(0)" : "translateX(24px)",
                    transition: "opacity 0.25s ease, transform 0.3s ease",
                    pointerEvents: isOpen ? "auto" : "none",
                }}
            >
                <Box
                    sx={{
                        padding: "24px 20px",
                        height: "100%",
                        overflowY: "auto",
                        "&::-webkit-scrollbar": { width: 0 },
                        scrollbarWidth: "none",
                    }}
                >
                    {imageUrl && (
                        <Box
                            component="img"
                            src={imageUrl}
                            alt={project?.name}
                            sx={{
                                width: "120px",
                                height: "120px",
                                borderRadius: "12px",
                                objectFit: "cover",
                                marginBottom: "14px",
                            }}
                        />
                    )}

                    <Typography
                        sx={{
                            fontSize: "17px",
                            fontWeight: 600,
                            color: "#0d0d0e",
                            fontFamily: "Inter Tight",
                            marginBottom: "8px",
                        }}
                    >
                        {project?.name || "-"}
                    </Typography>

                    <Box
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "4px 12px",
                            borderRadius: "999px",
                            background:
                                "linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)",
                            color: "#fff",
                            fontSize: "12px",
                            fontWeight: 600,
                            fontFamily: "Inter Tight",
                            marginBottom: "16px",
                        }}
                    >
                        <LuPalette size={13} />
                        Design
                    </Box>

                    <Typography
                        sx={{
                            fontSize: "12px",
                            color: "#949daa",
                            fontFamily: "Inter Tight",
                        }}
                    >
                        Proposal Amount
                    </Typography>
                    <Typography
                        component="span"
                        sx={{
                            fontSize: "20px",
                            fontWeight: 600,
                            color: "#0d0d0e",
                            fontFamily: "Inter Tight",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                            marginBottom: "16px",
                        }}
                    >
                        {Number(proposalAmount) > 0 ? (
                            <>
                                {renderCurrencyIconColored("black-small")}
                                {formattedAmountWithoutAed(
                                    Number(proposalAmount),
                                )}
                            </>
                        ) : (
                            "-"
                        )}
                    </Typography>

                    <Box sx={{ marginTop: "8px" }}>
                        <InfoRow label="Status" value={statusLabel} />
                        <InfoRow
                            label="Start Date"
                            value={project?.start_date}
                        />
                        <InfoRow label="End Date" value={project?.end_date} />
                        <InfoRow label="Stage" value={project?.stage} />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
